import React from 'react'
import { Step, type OrderCategory } from '../common'
import type { CartItem, Option, OrderInfo, Product } from 'types/products/productTypes'
import { type PastVisit } from 'types/types'
import { useAutoUpdatingRef, usePrevious } from '@quickmd-llc/frontend-utils'

export type StepData = ReturnType<typeof useSteps>

type Input = {
  isMoudDeliveryAvailable: boolean
  isMoudHomeDelivery: boolean
  visitId: string
  category: OrderCategory | undefined
  setSelectedOption: (option: Option | undefined) => void
  resetRefillsQuantity: () => void
  setProduct: StateSetter<Product>
  setOrderInfo: StateSetter<OrderInfo>
  orderInfo: OrderInfo
  visitList: PastVisit[]
  cartItems: CartItem[]
  setShowDiscardOrdersWarning: StateSetter<string>
}

export function useSteps({
  isMoudDeliveryAvailable,
  visitId,
  category,
  setSelectedOption,
  resetRefillsQuantity,
  setOrderInfo,
  setProduct,
  orderInfo,
  isMoudHomeDelivery,
  visitList,
  cartItems,
  setShowDiscardOrdersWarning,
}: Input) {
  const [currentStepId, setCurrentStepId] = React.useState(Step.VIEW_VISIT_LIST)

  const steps = React.useMemo(() => {
    // ** Note: should probably switch this to three separate arrays for the different flows so there is a clearer,
    // linear arrangment for each flow of all the steps that you don't have to kind of calculate mentally each time
    // looking at this for a given situation and can just see them all laid out beginning to end in entirety

    const isMHT = category === 'MenopauseHormoneTherapy'

    // TODO: this logic is a nightmare at this point. need to refactor
    const nulled = [
      visitList.length > 0 ? { id: Step.VIEW_VISIT_LIST, isEditStep: false } : null,
      isMoudDeliveryAvailable || isMHT ? { id: Step.VIEW_ORDER_DELIVERY_TYPE, isEditStep: false } : null,
      ...(isMoudHomeDelivery ?
        [{ id: Step.VIEW_MOUD_DELIVERY_COMBINED_PRODUCTS, isEditStep: true }]
      : [
          { id: Step.VIEW_PRODUCTS_LIST, isEditStep: isMHT },
          { id: Step.VIEW_OPTION_LIST, isEditStep: false },
        ]),
      isMoudHomeDelivery || isMHT ? null : { id: Step.VIEW_DOSE_DURATION, isEditStep: !isMoudHomeDelivery },
      isMoudHomeDelivery || isMHT ? { id: Step.VIEW_CART, isEditStep: false } : null,

      { id: Step.VIEW_COMPLETE_ORDER, isEditStep: false },
      { id: Step.VIEW_PAYMENT_ORDER, isEditStep: false },
    ]

    return nulled.filter(Boolean)
  }, [category, visitList.length, isMoudDeliveryAvailable, isMoudHomeDelivery])

  const stepsRef = useAutoUpdatingRef(steps)

  const goToStep = React.useCallback(
    (step: Step) => {
      const nextStep = stepsRef.current.find((s) => s.id === step)?.id
      // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
      setCurrentStepId(nextStep ?? stepsRef.current[0].id)
    },
    [setCurrentStepId, stepsRef],
  )

  const goToEdit = React.useCallback(() => {
    const editStep = stepsRef.current.find((x) => x.isEditStep) ?? stepsRef.current[0]
    // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
    goToStep(editStep.id)
  }, [stepsRef, goToStep])

  const goForward = React.useCallback(
    (from?: Step) => {
      const currentStepIndex = stepsRef.current.findIndex((step) => step.id === (from ?? currentStepId))
      const nextStepIndex = Math.min(stepsRef.current.length - 1, currentStepIndex + 1)
      // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
      const nextStep = stepsRef.current[nextStepIndex].id
      setCurrentStepId(nextStep)
    },
    [stepsRef, currentStepId, setCurrentStepId],
  )

  const goBack = React.useCallback(
    (skipCartCheck?: boolean) => {
      const currentStepIndex = stepsRef.current.findIndex((step) => step.id === currentStepId)
      const oneStepBackIndex = Math.max(0, currentStepIndex - 1)
      // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
      const oneStepBack = stepsRef.current[oneStepBackIndex].id
      if (oneStepBack === Step.VIEW_ORDER_DELIVERY_TYPE && cartItems.length && !skipCartCheck) {
        setShowDiscardOrdersWarning('back')
        return
      }
      if (oneStepBack === Step.VIEW_PRODUCTS_LIST || oneStepBack === Step.VIEW_MOUD_DELIVERY_COMBINED_PRODUCTS) {
        setSelectedOption(undefined)
        resetRefillsQuantity()
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        setProduct(null)
      } else if (oneStepBack === Step.VIEW_OPTION_LIST) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        setOrderInfo({
          ...orderInfo,
          DurationDays: undefined,
          DrugOptionId: undefined,
          DispenseAmount: undefined,
        })
      }
      setCurrentStepId(oneStepBack)
    },
    [
      stepsRef,
      cartItems.length,
      currentStepId,
      setShowDiscardOrdersWarning,
      setSelectedOption,
      resetRefillsQuantity,
      setProduct,
      setOrderInfo,
      orderInfo,
    ],
  )

  const restart = React.useCallback(() => {
    if (!visitId) {
      goToStep(Step.VIEW_VISIT_LIST)
    } else if (isMoudDeliveryAvailable || category === 'MenopauseHormoneTherapy') {
      goToStep(Step.VIEW_ORDER_DELIVERY_TYPE)
    } else {
      goToStep(Step.VIEW_PRODUCTS_LIST)
    }

    setSelectedOption(undefined)
    resetRefillsQuantity()
  }, [category, resetRefillsQuantity, setSelectedOption, visitId, isMoudDeliveryAvailable, goToStep])

  const position = React.useMemo(() => {
    // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
    const isFirst = currentStepId === steps[0].id
    // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
    const isLast = currentStepId === steps[steps.length - 1].id
    const currentIndex = steps.findIndex((x) => x.id === currentStepId)
    const totalSteps = steps.length
    return { isFirst, isLast, currentIndex, totalSteps, currentStepId }
  }, [steps, currentStepId])

  const previousSteps = usePrevious(steps)

  // if the steps changed and the step we are on/were on was what used to be the first step, just restart as it is
  // likely new data came in that added new steps to the beginning of the array (which can happen when the products data
  // or visit data comes in) and we probably still want to just be at the beginning of the flow. this is a bit of a band
  // aid to get this out asap but before the full launch of reorders we should add a check that ensures all the data we
  // need exists prior to the popup/steps being instantiated and the flow kicking off
  const userShouldBeMovedBackToBeginningOfFlow =
    previousSteps !== steps &&
    previousSteps?.findIndex((x) => x.id === currentStepId) === 0 &&
    position.currentIndex !== 0

  React.useEffect(() => {
    if (userShouldBeMovedBackToBeginningOfFlow) {
      restart()
    }
  }, [userShouldBeMovedBackToBeginningOfFlow, restart])

  return { goForward, goBack, restart, goToStep, goToEdit, currentStepId, position }
}
